<template>
  <button
    @mouseover="onMouseOver()"
    @mouseout="onMouseOut()"
    @click="action"
    :class="
      variant === 'primary' || variant === 'secondary'
        ? `btn-${variant}`
        : variant
    "
    type="button"
    :style="noLeftPadding ? 'padding-left: 4px;' : ''"
    :disabled="disabled"
  >
    <slot></slot>
  </button>
</template>

<script>
//Usage: <Button variant="(primary,secondary,outline,textonly,danger,toggle-on,toggle-off)">Slot Contents</Button>
export default {
  name: "Button",
  props: {
    action: {
      type: Function,
      default: () => {}
    },
    onMouseOver: {
      type: Function,
      default: () => {}
    },
    onMouseOut: {
      type: Function,
      default: () => {}
    },
    disabled: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: "primary",
      validator: val =>
        [
          "primary",
          "primary-fullwidth",
          "primary-player",
          "secondary",
          "secondary-fullwidth",
          "outline",
          "textonly",
          "danger",
          "toggle-on",
          "toggle-off",
          "white-fullwidth"
        ].includes(val),
      description: "Selected variant"
    },
    noLeftPadding: { type: Boolean, required: false, default: false }
  },
  data: () => ({}),
  computed: {}
};
</script>

<style lang="scss" scoped>
%button-common {
  height: 40px;
  border-radius: 6px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 16px;
  padding-right: 16px;
  font-family: "objektiv-mk2", sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 14px;
  line-height: 24px;
  border: none;
  cursor: pointer;
  &:focus {
    outline: none;
  }
}
.btn-primary {
  @extend %button-common;
  width: auto;
  background: #6139b8;
  color: #f2eeff;
}
.btn-primary:hover {
  @extend %button-common;
  background: #7c55d2;
  color: #f2eeff;
}
.btn-primary-player {
  @extend %button-common;
  width: auto;
  background: #6139b8;
  color: #f2eeff;
}
.btn-primary-player:hover {
  @extend %button-common;
  font-size: 12px;
  line-height: 22px;
}
.btn-primary:disabled {
  @extend %button-common;
  background: #7c55d2;
  color: rgba(209, 194, 255, 0.7);
  cursor: not-allowed;
}
.btn-secondary {
  @extend %button-common;
  width: auto;
  background: #272632;
  color: #f2eeff;
}
.btn-secondary:hover {
  @extend %button-common;
  background: #3c374a;
  color: #f2eeff;
}
.btn-secondary:disabled {
  @extend %button-common;
  background: #191922;
  color: rgba(209, 194, 255, 0.7);
  cursor: not-allowed;
}
.outline {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #6139b8;
  box-sizing: border-box;
  width: auto;
  background: inherit;
  color: #f2eeff;
}
.outline:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #7c55d2;
  box-sizing: border-box;
  background: inherit;
  color: #f2eeff;
}
.outline:disabled {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #362468;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.textonly {
  @extend %button-common;
  box-sizing: border-box;
  width: auto;
  background: inherit;
  color: #8f7af0;
}
.textonly:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #362468;
  box-sizing: border-box;
  background: inherit;
  color: #8f7af0;
}
.textonly:disabled {
  @extend %button-common;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.danger {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #f81b55;
  box-sizing: border-box;
  width: auto;
  background: inherit;
  color: #f2eeff;
}
.danger:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #f81b55;
  box-sizing: border-box;
  background: inherit;
  color: #f2eeff;
}
.danger:disabled {
  @extend %button-common;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.primary-fullwidth {
  @extend %button-common;
  width: 100%;
  background: #6139b8;
  color: #f2eeff;
}
.primary-fullwidth:hover {
  @extend %button-common;
  background: #7c55d2;
  color: #f2eeff;
}
.primary-fullwidth:disabled {
  @extend %button-common;
  background: #4d2f8d;
  color: rgba(209, 194, 255, 0.7);
  cursor: not-allowed;
}
.secondary-fullwidth {
  @extend %button-common;
  width: 100%;
  background: #272632;
  color: #f2eeff;
}
.secondary-fullwidth:hover {
  @extend %button-common;
  background: #3c374a;
  color: #f2eeff;
}
.secondary-fullwidth:disabled {
  @extend %button-common;
  background: #191922;
  color: rgba(209, 194, 255, 0.7);
  cursor: not-allowed;
}
.outline-fullwidth {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #6139b8;
  box-sizing: border-box;
  width: 100%;
  background: inherit;
  color: #f2eeff;
}
.outline-fullwidth:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #7c55d2;
  box-sizing: border-box;
  background: inherit;
  color: #f2eeff;
}
.outline-fullwidth:disabled {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #362468;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.textonly-fullwidth {
  @extend %button-common;
  box-sizing: border-box;
  width: 100%;
  background: inherit;
  color: #8f7af0;
}
.textonly-fullwidth:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #362468;
  box-sizing: border-box;
  background: inherit;
  color: #8f7af0;
}
.textonly-fullwidth:disabled {
  @extend %button-common;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.danger-fullwidth {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #f81b55;
  box-sizing: border-box;
  width: 100%;
  background: inherit;
  color: #f2eeff;
}
.danger-fullwidth:hover {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #f81b55;
  box-sizing: border-box;
  background: inherit;
  color: #f2eeff;
}
.danger-fullwidth:disabled {
  @extend %button-common;
  box-sizing: border-box;
  color: rgba(209, 194, 255, 0.5);
  cursor: not-allowed;
}
.toggle-off {
  @extend %button-common;
  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0), inset 0 0 0 2px #2d2b33;
  box-sizing: border-box;
  background: inherit;
  color: #f2eeff;
}
.toggle-on {
  @extend %button-common;
  width: auto;
  background: #6139b8;
  color: #f2eeff;
}
.white {
  @extend %button-common;
  width: auto;
  background: #ffffff;
  color: #000000;
}
.white:hover {
  @extend %button-common;
  background: #ffffff;
  color: #000000;
}
.white-fullwidth {
  @extend %button-common;
  width: 100%;
  background: #ffffff;
  color: #000000;
}
.white-fullwidth:hover {
  @extend %button-common;
  background: #ffffff;
  color: #000000;
}
</style>
